import React, { useState } from "react";
import { toast } from "sonner";
import { Loader2, Handshake } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import PageHero from "@/components/PageHero";
import { Container, GoldButton } from "@/components/shared";

const intents = [
  { key: "trade", label: "Trade", body: "Buying, selling, or supplying across the shores." },
  { key: "education", label: "Education", body: "Schools, talks, exchanges, and research." },
  { key: "residency", label: "Residency", body: "Makers, artists, or scholars seeking a stay." },
  { key: "press", label: "Press", body: "Journalists and broadcasters covering the crossing." },
];

export default function Exchange() {
  const [form, setForm] = useState({ name: "", email: "", shore: "lincolnshire", intent: "trade", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/introductions", form);
      setSent(true);
      toast.success("Your introduction has been logged for the crossing.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <div>
      <PageHero
        kicker="Exchange"
        title="Ask for an introduction"
        intro="The crossing is more than a directory. If you are looking to trade, teach, visit, or report across the two Bostons, leave your details and we will try to put the right people in touch."
      />

      <section className="py-16 sm:py-24">
        <Container>
          <div className="grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <div className="w-12 h-12 border border-[#CDA434] flex items-center justify-center text-[#CDA434] mb-6">
                <Handshake size={22} />
              </div>
              <h2 className="font-serif text-2xl text-[#1E1E2F] mb-4">A handshake across the water</h2>
              <p className="font-sans text-base text-[#1B2430] leading-relaxed mb-4">
                Introductions are made by hand, quietly, by a private citizen — not by any council or
                official body. We cannot promise a match, only that we will read every note and
                connect what we sensibly can.
              </p>
              <p className="font-sans text-sm text-[#505A68] leading-relaxed">
                In this preview, your introduction is stored and appears in the admin ledger.
              </p>
            </div>

            <div className="lg:col-span-7">
              {sent ? (
                <div data-testid="exchange-success" className="bg-[#FFFAF3] border border-[#CDA434] p-10 text-center">
                  <p className="font-playfair italic text-xl text-[#7a611c] mb-3">Your introduction has been logged for the crossing.</p>
                  <p className="font-sans text-sm text-[#505A68]">We will be in touch if a fitting connection can be made.</p>
                  <button onClick={() => { setSent(false); setForm({ name: "", email: "", shore: "lincolnshire", intent: "trade", message: "" }); }} className="mt-5 font-sans text-sm font-semibold text-[#003366] link-underline">Send another</button>
                </div>
              ) : (
                <form onSubmit={submit} className="bg-[#FFFAF3] border border-[#D6D3CB] p-8 space-y-5" data-testid="exchange-form">
                  <div className="grid sm:grid-cols-2 gap-5">
                    <Field label="Your name"><input required data-testid="exchange-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} /></Field>
                    <Field label="Your email"><input required type="email" data-testid="exchange-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} /></Field>
                  </div>
                  <Field label="Which shore are you on?">
                    <select data-testid="exchange-shore" value={form.shore} onChange={(e) => setForm({ ...form, shore: e.target.value })} className={inputCls}>
                      <option value="lincolnshire">Lincolnshire</option>
                      <option value="massachusetts">Massachusetts</option>
                      <option value="both">Both / elsewhere</option>
                    </select>
                  </Field>
                  <Field label="What kind of introduction?">
                    <div className="grid sm:grid-cols-2 gap-3">
                      {intents.map((it) => (
                        <button type="button" key={it.key} data-testid={`exchange-intent-${it.key}`} onClick={() => setForm({ ...form, intent: it.key })}
                          className={`text-left p-4 border transition-colors ${form.intent === it.key ? "border-[#CDA434] bg-[#CDA434]/10" : "border-[#D6D3CB] hover:border-[#CDA434]"}`}>
                          <span className="font-serif text-base text-[#1E1E2F] block">{it.label}</span>
                          <span className="font-sans text-xs text-[#505A68]">{it.body}</span>
                        </button>
                      ))}
                    </div>
                  </Field>
                  <Field label="Tell us a little more">
                    <textarea required rows={4} data-testid="exchange-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className={`${inputCls} resize-none`} />
                  </Field>
                  <GoldButton type="submit" disabled={sending} data-testid="exchange-submit" className="w-full">
                    {sending ? <Loader2 size={16} className="animate-spin" /> : "Request an introduction"}
                  </GoldButton>
                </form>
              )}
            </div>
          </div>
        </Container>
      </section>
    </div>
  );
}

const inputCls = "w-full bg-[#FFFAF3] border border-[#D6D3CB] focus:border-[#003366] px-4 py-2.5 text-sm font-sans text-[#1B2430]";
function Field({ label, children }) {
  return (
    <div>
      <label className="block font-sans text-xs uppercase tracking-wider font-semibold text-[#1B2430] mb-1.5">{label}</label>
      {children}
    </div>
  );
}
